import { useCallback, useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";

type Kind = "logo" | "cover";

interface Props {
  file: File | null;
  kind: Kind;
  onCancel: () => void;
  onConfirm: (cropped: Blob) => void;
  busy?: boolean;
}

const ASPECT: Record<Kind, number> = { logo: 1, cover: 3 };
const OUTPUT: Record<Kind, { w: number; h: number }> = {
  logo: { w: 600, h: 600 },
  cover: { w: 1500, h: 500 },
};

async function cropToBlob(
  imageSrc: string,
  area: Area,
  outW: number,
  outH: number,
  mimeType: string,
): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      mimeType,
      mimeType === "image/jpeg" ? 0.9 : undefined,
    );
  });
}

function pickOutputMime(kind: Kind, sourceMime: string | undefined): string {
  if (kind === "logo") {
    if (sourceMime === "image/png" || sourceMime === "image/webp") return sourceMime;
    return "image/png";
  }
  return "image/jpeg";
}

export function BrandingCropDialog({ file, kind, onCancel, onConfirm, busy }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!file) {
      setSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setArea(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!src || !area || !file) return;
    setWorking(true);
    try {
      const out = OUTPUT[kind];
      const mime = pickOutputMime(kind, file.type);
      const blob = await cropToBlob(src, area, out.w, out.h, mime);
      onConfirm(blob);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not crop image";
      toast({ title: "Crop failed", description: message, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const open = !!file;
  const isBusy = busy || working;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o && !isBusy) onCancel(); }}>
      <DialogContent
        className="bg-zinc-900 border-zinc-800 text-white max-w-lg p-0 overflow-hidden"
        data-testid={`dialog-crop-${kind}`}
      >
        <DialogHeader className="px-5 pt-5">
          <DialogTitle data-testid={`text-crop-title-${kind}`}>
            {kind === "logo" ? "Crop logo" : "Crop cover photo"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Drag to reposition. Use the slider to zoom.
          </DialogDescription>
        </DialogHeader>
        <div
          className="relative w-full bg-black"
          style={{ height: kind === "logo" ? 360 : 280 }}
          data-testid={`crop-stage-${kind}`}
        >
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT[kind]}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </div>
        <div className="px-5 py-3">
          <label className="text-xs text-zinc-400">Zoom</label>
          <Slider
            value={[zoom]}
            min={1}
            max={4}
            step={0.01}
            onValueChange={v => setZoom(v[0])}
            data-testid={`slider-crop-zoom-${kind}`}
            className="mt-2"
          />
        </div>
        <DialogFooter className="px-5 pb-5 gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isBusy}
            data-testid={`button-crop-cancel-${kind}`}
            className="text-zinc-300 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isBusy || !area}
            data-testid={`button-crop-confirm-${kind}`}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {isBusy ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
